import { getApiOrigin } from "@utils/apiOrigin";

export interface SsoButtonsSettingsResponse {
  success?: boolean;
  message?: string;
  status_code?: number;
  response?: {
    login_sso?: boolean;
    signup_sso?: boolean;
  };
}

export interface SsoButtonsSettings {
  login_sso: boolean;
  signup_sso: boolean;
}

const SSO_BUTTONS_SETTINGS_PATH = "/controls/sso-buttons/settings";

/** Same default as Rules APP_ENABLE_SSO_BUTTONS — show when settings are unreachable. */
const DEFAULT_SSO_BUTTONS: SsoButtonsSettings = {
  login_sso: true,
  signup_sso: true,
};

let cachedSettings: SsoButtonsSettings | null = null;
let pendingSettingsPromise: Promise<SsoButtonsSettings> | null = null;

function isJsonContentType(contentType: string | null): boolean {
  return (contentType || "").toLowerCase().includes("application/json");
}

export async function getSsoButtonSettings(
  forceRefresh = false
): Promise<SsoButtonsSettings> {
  if (!forceRefresh && cachedSettings) {
    return cachedSettings;
  }

  if (!forceRefresh && pendingSettingsPromise) {
    return pendingSettingsPromise;
  }

  pendingSettingsPromise = (async () => {
    const baseUrl = getApiOrigin();
    const candidateUrls: string[] = [];
    const addCandidate = (url: string) => {
      if (!candidateUrls.includes(url)) {
        candidateUrls.push(url);
      }
    };

    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      addCandidate(`${normalizedBase}${SSO_BUTTONS_SETTINGS_PATH}`);
    }

    // Same-origin /controls/... on Hub is the SPA shell (HTML 200), not Rules.

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        const contentType = response.headers?.get?.("content-type") ?? null;
        if (!response.ok || (contentType !== null && !isJsonContentType(contentType))) {
          continue;
        }
        const data = (await response.json()) as SsoButtonsSettingsResponse;
        if (!data?.response || typeof data.response !== "object") {
          continue;
        }
        const settings: SsoButtonsSettings = {
          login_sso: data.response.login_sso !== false,
          signup_sso: data.response.signup_sso !== false,
        };
        cachedSettings = settings;
        return settings;
      } catch {
        // Try next candidate URL.
      }
    }

    return DEFAULT_SSO_BUTTONS;
  })();

  try {
    return await pendingSettingsPromise;
  } finally {
    pendingSettingsPromise = null;
  }
}
